import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { format } from 'date-fns';
import { CalendarIcon, Plus, X, Save } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import VoiceNote from './VoiceNote';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DynamicList = ({ items, onChange, placeholder }) => {
  const addItem = () => {
    onChange([...items, '']);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex gap-2">
          <Input
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            placeholder={placeholder}
            className="flex-1"
            data-testid={`dynamic-list-input-${index}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeItem(index)}
            className="text-slate-400 hover:text-red-500"
            data-testid={`remove-item-${index}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addItem}
        className="gap-1 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
        data-testid="add-list-item"
      >
        <Plus className="w-4 h-4" />
        Add Entry
      </Button>
    </div>
  );
};

const DynamicTable = ({ rows, onChange, nameLabel = "Name", remarksLabel = "Remarks", middleLabel = null, middleField = null }) => {
  const addRow = () => {
    const newRow = { name: '', remarks: '' };
    if (middleField) newRow[middleField] = '';
    onChange([...rows, newRow]);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    onChange(newRows);
  };

  const gridCols = middleLabel ? 'grid-cols-[1fr_1fr_1fr_40px]' : 'grid-cols-[1fr_1fr_40px]';

  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        <div className={`grid ${gridCols} gap-2 text-sm font-medium text-slate-500 px-1`}>
          <span>{nameLabel}</span>
          {middleLabel && <span>{middleLabel}</span>}
          <span>{remarksLabel}</span>
          <span></span>
        </div>
      )}
      {rows.map((row, index) => (
        <div key={index} className={`grid ${gridCols} gap-2`}>
          <Input
            value={row.name}
            onChange={(e) => updateRow(index, 'name', e.target.value)}
            placeholder={nameLabel}
            data-testid={`table-name-${index}`}
          />
          {middleField && (
            <Input
              value={row[middleField] || ''}
              onChange={(e) => updateRow(index, middleField, e.target.value)}
              placeholder={middleLabel}
              data-testid={`table-${middleField}-${index}`}
            />
          )}
          <Input
            value={row.remarks}
            onChange={(e) => updateRow(index, 'remarks', e.target.value)}
            placeholder={remarksLabel}
            data-testid={`table-remarks-${index}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeRow(index)}
            className="text-slate-400 hover:text-red-500"
            data-testid={`remove-row-${index}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        className="gap-1 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
        data-testid="add-table-row"
      >
        <Plus className="w-4 h-4" />
        Add Row
      </Button>
    </div>
  );
};

const KeyContactsTable = ({ rows, onChange }) => {
  const addRow = () => {
    onChange([...rows, { name: '', company: '' }]);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    onChange(newRows);
  };

  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        <div className="grid grid-cols-[1fr_1fr_40px] gap-2 text-sm font-medium text-slate-500 px-1">
          <span>Name</span>
          <span>Company</span>
          <span></span>
        </div>
      )}
      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-[1fr_1fr_40px] gap-2">
          <Input
            value={row.name}
            onChange={(e) => updateRow(index, 'name', e.target.value)}
            placeholder="Name"
            data-testid={`key-contact-name-${index}`}
          />
          <Input
            value={row.company}
            onChange={(e) => updateRow(index, 'company', e.target.value)}
            placeholder="Company"
            data-testid={`key-contact-company-${index}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeRow(index)}
            className="text-slate-400 hover:text-red-500"
            data-testid={`remove-key-contact-${index}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        className="gap-1 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
        data-testid="add-key-contact"
      >
        <Plus className="w-4 h-4" />
        Add Row
      </Button>
    </div>
  );
};

const TagInput = ({ tags, onChange, suggestions = [] }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredSuggestions = suggestions.filter(
    s => s.toLowerCase().includes(inputValue.toLowerCase()) && !tags.includes(s)
  );

  const addTag = (tag) => {
    if (tag && !tags.includes(tag)) {
      onChange([...tags, tag]);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (tagToRemove) => {
    onChange(tags.filter(t => t !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();
      addTag(inputValue);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="tag flex items-center gap-1"
            data-testid={`tag-${index}`}
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-red-500"
              data-testid={`remove-tag-${index}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder="Type and press Enter to add tag"
          data-testid="tag-input"
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => addTag(suggestion)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                data-testid={`tag-suggestion-${index}`}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ContactForm = ({ contact, editingInteraction, onSubmit, onCancel }) => {
  const { getAuthHeader } = useAuth();
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    current_employer: '',
    job_title: '',
    designation: '',
    city_based_in: '',
    email_address: '',
    phone_numbers: {
      handphone: { country_code: '', number: '' },
      work: { country_code: '', number: '' }
    },
    brief_background: [],
    former_employers: ['', ''],
    common_network: [],
    hobbies_passions: [],
    family_information: [],
    other_remarks: [],
    key_contacts_in_network: [],
    next_course_of_action: [],
    custom_tags: [],
    // First interaction
    first_interaction: {
      interaction_type: 'meeting',
      date: '',
      city: '',
      location: '',
      setup_method: '',
      connected_by: '',
      connected_by_company: '',
      key_points: '',
      questions_asked: '',
      quotes: '',
      future_course_of_action: ''
    }
  });
  const [showInteractionDate, setShowInteractionDate] = useState(false);

  useEffect(() => {
    // Fetch tag suggestions
    const fetchTags = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/tags`, getAuthHeader());
        setTagSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, [getAuthHeader]);

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        current_employer: contact.current_employer || '',
        job_title: contact.job_title || '',
        designation: contact.designation || '',
        city_based_in: contact.city_based_in || '',
        email_address: contact.email_address || '',
        phone_numbers: contact.phone_numbers || {
          handphone: { country_code: '', number: '' },
          work: { country_code: '', number: '' }
        },
        brief_background: contact.brief_background || [],
        former_employers: contact.former_employers?.length ? contact.former_employers : ['', ''],
        common_network: contact.common_network || [],
        hobbies_passions: contact.hobbies_passions || [],
        family_information: contact.family_information || [],
        other_remarks: contact.other_remarks || [],
        key_contacts_in_network: contact.key_contacts_in_network || [],
        next_course_of_action: contact.next_course_of_action || [],
        custom_tags: contact.custom_tags || [],
        first_interaction: (editingInteraction && !editingInteraction._isNew) ? {
          interaction_type: editingInteraction.interaction_type || 'meeting',
          date: editingInteraction.date || '',
          city: editingInteraction.city || '',
          location: editingInteraction.location || '',
          setup_method: editingInteraction.setup_method || '',
          connected_by: editingInteraction.connected_by || '',
          connected_by_company: editingInteraction.connected_by_company || '',
          key_points: editingInteraction.key_points || '',
          questions_asked: editingInteraction.questions_asked || '',
          quotes: editingInteraction.quotes || '',
          future_course_of_action: editingInteraction.future_course_of_action || ''
        } : {
          interaction_type: 'meeting',
          date: '',
          city: '',
          location: '',
          setup_method: '',
          connected_by: '',
          connected_by_company: '',
          key_points: '',
          questions_asked: '',
          quotes: '',
          future_course_of_action: ''
        }
      });
    }
  }, [contact, editingInteraction]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updatePhoneNumber = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      phone_numbers: {
        ...prev.phone_numbers,
        [type]: {
          ...prev.phone_numbers[type],
          [field]: value
        }
      }
    }));
  };

  const updateInteraction = (field, value) => {
    setFormData(prev => ({
      ...prev,
      first_interaction: {
        ...prev.first_interaction,
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare submission data
    const submitData = {
      ...formData,
      former_employers: formData.former_employers.filter(e => e.trim())
    };
    
    if (editingInteraction && !editingInteraction._isNew) {
      // Editing an existing interaction
      submitData._editingInteraction = formData.first_interaction;
      submitData._editingInteractionId = editingInteraction.id;
      delete submitData.first_interaction;
    } else if (editingInteraction && editingInteraction._isNew) {
      // Adding a new interaction to an existing contact
      submitData._newInteraction = formData.first_interaction;
      delete submitData.first_interaction;
    } else if (!contact && (formData.first_interaction.date || formData.first_interaction.key_points)) {
      // New contact with first interaction data
      submitData.first_interaction = formData.first_interaction;
    } else {
      delete submitData.first_interaction;
    }
    
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 animate-fadeIn">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              placeholder="Full name"
              data-testid="contact-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Current Employer</Label>
            <Input
              value={formData.current_employer}
              onChange={(e) => updateField('current_employer', e.target.value)}
              placeholder="Company name"
              data-testid="contact-employer-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Job Title</Label>
            <Input
              value={formData.job_title}
              onChange={(e) => updateField('job_title', e.target.value)}
              placeholder="e.g., Managing Director"
              data-testid="contact-jobtitle-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Designation</Label>
            <Input
              value={formData.designation}
              onChange={(e) => updateField('designation', e.target.value)}
              placeholder="e.g., Partner"
              data-testid="contact-designation-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">City Based In</Label>
            <Input
              value={formData.city_based_in}
              onChange={(e) => updateField('city_based_in', e.target.value)}
              placeholder="e.g., Singapore"
              data-testid="contact-city-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Email Address</Label>
            <Input
              type="email"
              value={formData.email_address}
              onChange={(e) => updateField('email_address', e.target.value)}
              placeholder="email@company.com"
              data-testid="contact-email-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Handphone</Label>
            <div className="flex gap-2">
              <Input
                value={formData.phone_numbers.handphone.country_code}
                onChange={(e) => updatePhoneNumber('handphone', 'country_code', e.target.value)}
                placeholder="+65"
                className="w-20"
                data-testid="handphone-code-input"
              />
              <Input
                value={formData.phone_numbers.handphone.number}
                onChange={(e) => updatePhoneNumber('handphone', 'number', e.target.value)}
                placeholder="9123 4567"
                className="flex-1"
                data-testid="handphone-number-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Work Phone</Label>
            <div className="flex gap-2">
              <Input
                value={formData.phone_numbers.work.country_code}
                onChange={(e) => updatePhoneNumber('work', 'country_code', e.target.value)}
                placeholder="+65"
                className="w-20"
                data-testid="work-code-input"
              />
              <Input
                value={formData.phone_numbers.work.number}
                onChange={(e) => updatePhoneNumber('work', 'number', e.target.value)}
                placeholder="6789 0123"
                className="flex-1"
                data-testid="work-number-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Interaction Section - for new contacts OR when editing/adding an interaction */}
      {(!contact || editingInteraction) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
              {editingInteraction?._isNew ? 'New Interaction' : editingInteraction ? 'Edit Interaction' : 'First Interaction'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label className="text-slate-700 font-medium">Meeting or Call</Label>
              <RadioGroup
                value={formData.first_interaction.interaction_type}
                onValueChange={(value) => updateInteraction('interaction_type', value)}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="meeting" id="meeting" data-testid="interaction-meeting" />
                  <Label htmlFor="meeting" className="cursor-pointer">Meeting</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="call" id="call" data-testid="interaction-call" />
                  <Label htmlFor="call" className="cursor-pointer">Call</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Date</Label>
                <Popover open={showInteractionDate} onOpenChange={setShowInteractionDate}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="interaction-date-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.first_interaction.date 
                        ? format(new Date(formData.first_interaction.date), 'PPP')
                        : 'Select date'
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.first_interaction.date ? new Date(formData.first_interaction.date) : undefined}
                      onSelect={(date) => {
                        updateInteraction('date', date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '');
                        setShowInteractionDate(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {formData.first_interaction.interaction_type === 'meeting' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">City of Meeting</Label>
                    <Input
                      value={formData.first_interaction.city}
                      onChange={(e) => updateInteraction('city', e.target.value)}
                      placeholder="e.g., Hong Kong"
                      data-testid="meeting-city-input"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-slate-700 font-medium">Location</Label>
                    <Input
                      value={formData.first_interaction.location}
                      onChange={(e) => updateInteraction('location', e.target.value)}
                      placeholder="e.g., Mandarin Oriental"
                      data-testid="meeting-location-input"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-3">
              <Label className="text-slate-700 font-medium">How was this set up?</Label>
              <RadioGroup
                value={formData.first_interaction.setup_method}
                onValueChange={(value) => updateInteraction('setup_method', value)}
                className="space-y-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="reached_out_by_them" id="them" data-testid="setup-them" />
                  <Label htmlFor="them" className="cursor-pointer">He/She reached out</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="reached_out_by_me" id="me" data-testid="setup-me" />
                  <Label htmlFor="me" className="cursor-pointer">I reached out</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="connected_by" id="connected" data-testid="setup-connected" />
                  <Label htmlFor="connected" className="cursor-pointer">Connected by someone</Label>
                </div>
              </RadioGroup>
              {formData.first_interaction.setup_method === 'connected_by' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input
                    value={formData.first_interaction.connected_by}
                    onChange={(e) => updateInteraction('connected_by', e.target.value)}
                    placeholder="Connected by whom?"
                    data-testid="connected-by-input"
                  />
                  <Input
                    value={formData.first_interaction.connected_by_company}
                    onChange={(e) => updateInteraction('connected_by_company', e.target.value)}
                    placeholder="Their company"
                    data-testid="connected-by-company-input"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 font-medium">Key Points of Meeting</Label>
                <VoiceNote
                  onTranscription={(text) => {
                    const current = formData.first_interaction.key_points || '';
                    const separator = current ? '\n' : '';
                    updateInteraction('key_points', current + separator + text);
                  }}
                />
              </div>
              <RichTextEditor
                value={formData.first_interaction.key_points}
                onChange={(value) => updateInteraction('key_points', value)}
                placeholder="Add detailed notes about the meeting..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Questions Asked <span className="text-slate-400 font-normal">(optional)</span></Label>
              <RichTextEditor
                value={formData.first_interaction.questions_asked}
                onChange={(value) => updateInteraction('questions_asked', value)}
                placeholder="Questions asked during the meeting..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Quotes <span className="text-slate-400 font-normal">(if any)</span></Label>
              <RichTextEditor
                value={formData.first_interaction.quotes}
                onChange={(value) => updateInteraction('quotes', value)}
                placeholder="Notable quotes from the meeting..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Future Course of Action</Label>
              <RichTextEditor
                value={formData.first_interaction.future_course_of_action}
                onChange={(value) => updateInteraction('future_course_of_action', value)}
                placeholder="Next steps, follow-ups, action items..."
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Background */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
            Background
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Brief Background</Label>
            <DynamicList
              items={formData.brief_background}
              onChange={(items) => updateField('brief_background', items)}
              placeholder="Add background point (e.g., Family office based in Montreal)"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Former Employers</Label>
            <div className="space-y-2">
              <Input
                value={formData.former_employers[0] || ''}
                onChange={(e) => {
                  const newEmployers = [...formData.former_employers];
                  newEmployers[0] = e.target.value;
                  updateField('former_employers', newEmployers);
                }}
                placeholder="Former Employer 1"
                data-testid="former-employer-1"
              />
              <Input
                value={formData.former_employers[1] || ''}
                onChange={(e) => {
                  const newEmployers = [...formData.former_employers];
                  newEmployers[1] = e.target.value;
                  updateField('former_employers', newEmployers);
                }}
                placeholder="Former Employer 2"
                data-testid="former-employer-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Network & Personal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
            Network & Personal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Common Network</Label>
            <DynamicTable
              rows={formData.common_network}
              onChange={(rows) => updateField('common_network', rows)}
              nameLabel="Name"
              remarksLabel="Remarks"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Hobbies & Passions</Label>
            <DynamicTable
              rows={formData.hobbies_passions}
              onChange={(rows) => updateField('hobbies_passions', rows)}
              nameLabel="Hobby / Passion"
              remarksLabel="Remarks"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Family Information</Label>
            <DynamicTable
              rows={formData.family_information}
              onChange={(rows) => updateField('family_information', rows)}
              nameLabel="Family Member"
              middleLabel="Relationship"
              middleField="relationship"
              remarksLabel="Remarks"
            />
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading text-[#0F172A] border-b border-[#D4AF37]/30 pb-2">
            Additional Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Other Remarks</Label>
            <DynamicList
              items={formData.other_remarks}
              onChange={(items) => updateField('other_remarks', items)}
              placeholder="Add remark"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Key Contacts in Network <span className="text-slate-400 font-normal">(optional)</span></Label>
            <KeyContactsTable
              rows={formData.key_contacts_in_network}
              onChange={(rows) => updateField('key_contacts_in_network', rows)}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Next Course of Action</Label>
            <DynamicList
              items={formData.next_course_of_action}
              onChange={(items) => updateField('next_course_of_action', items)}
              placeholder="Add action item"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Custom Tags</Label>
            <TagInput
              tags={formData.custom_tags}
              onChange={(tags) => updateField('custom_tags', tags)}
              suggestions={tagSuggestions}
            />
          </div>
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          data-testid="form-cancel-button"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white"
          data-testid="form-submit-button"
        >
          <Save className="w-4 h-4" />
          {contact ? 'Update Contact' : 'Save Contact'}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
